import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import * as core from '@fortune-sheet/core';
import { getSheetIndex } from '@fortune-sheet/core';
import isEmpty from 'lodash/isEmpty';
import { splitSparseTail } from './sparseTail';
import { toCelldataSheet } from './converters';
import { findUncachedFormulas, holdDirtySuppression, recalculateUntilStable, UncachedFormula } from './recalcFormulas';
import type { FortuneCellData, FortuneSheet } from './types';
import { getInternals, produceQuietly, refreshRowColOffsets } from './excel/workbookInternals';
import {
    commitPreparedSheet,
    createSheetBuild,
    freezeSheetPayload,
    HYDRATE_SLICE_MS,
    PreparedSheet,
    prepareFormulasInSlices,
    runInSlices,
} from './sheetHydration';
import {
    addRowHeights,
    createRowAutofitJob,
    createTextHeightMeasure,
    keepRowsOnScreen,
    shiftAutoRowHeights,
    withoutAutoRowHeights,
} from './rowAutofit';

/**
 * Above this many total cells, non-active sheets mount as stubs and hydrate
 * right after first paint. Fortune-Sheet eagerly expands EVERY sheet's celldata
 * into a dense row×column matrix (plus a formula-map pass) at mount, so a large
 * multi-tab workbook pays the full cost before anything renders.
 */
export const LAZY_HYDRATION_CELL_THRESHOLD = 50_000;

// Stub grid dimensions — small enough that the eager dense-matrix expansion of
// a stub costs nothing. updateSheet expands the cells to the payload's size but
// keeps the stub's row/column, so the hydration recipe restores them.
const STUB_ROWS = 50;
const STUB_COLS = 26;

// Hydration cadence: wait a beat after mount so the first paint lands, then
// fill one sheet at a time, each in short slices (sheetHydration.ts).
const FIRST_HYDRATE_DELAY_MS = 120;
const HYDRATE_TICK_MS = 30;
// After an edit that leaves the formula map empty (a row/column insert or delete, or its undo), prepare it again
// once the edits pause, so the next edit does not rebuild it (0.4 s on a 47k-formula workbook, 1.5 s at 4x CPU).
const FORMULA_PREP_AFTER_EDIT_MS = 400;
// Open-time row fit: the measured heights go in after the first slice, then every so often until the sheet is done.
const ROW_AUTOFIT_FIRST_APPLY_MS = 250;
const ROW_AUTOFIT_APPLY_MS = 2000;

/** A trimmed sheet's calcChain must not point past its shrunken grid: Fortune's
 *  mount-time formula pass throws on calcChain cells outside the data matrix.
 *  Tail formulas keep their `f` and cached value in the parked cells and are not
 *  recalculated while the sheet is open; a kept formula that references a parked
 *  cell reads it as empty. */
const clipCalcChainToGrid = (sheet: FortuneSheet): FortuneSheet => {
    if (!sheet.calcChain?.length || sheet.row == null) return sheet;
    const rowCount = sheet.row;
    return { ...sheet, calcChain: sheet.calcChain.filter((entry) => entry.r < rowCount) };
};

const totalCells = (sheets: FortuneSheet[]): number => sheets.reduce((n, s) => n + (s.celldata?.length ?? 0), 0);

/** Exported for tests. A sheet saved after an older lazy hydration can carry the
 *  50×26 stub size with cells beyond it; Ctrl+Arrow, column auto-fit and the tail
 *  trim read row/column as the grid's edge. Same reference when already covering. */
export const withCoveringDims = (sheet: FortuneSheet): FortuneSheet => {
    if (sheet.row == null || sheet.column == null) return sheet;
    let rows = sheet.data?.length ?? 0;
    let cols = sheet.data?.[0]?.length ?? 0;
    (sheet.celldata ?? []).forEach((cell) => {
        if (cell.r >= rows) rows = cell.r + 1;
        if (cell.c >= cols) cols = cell.c + 1;
    });
    if (rows <= sheet.row && cols <= sheet.column) return sheet;
    return { ...sheet, row: Math.max(sheet.row, rows), column: Math.max(sheet.column, cols) };
};

/** Exported for tests. Keeps id/name/order/config (styling metadata is cheap and
 *  survives hydration — updateSheet's celldata path only swaps cell content in),
 *  drops the cell payload and the calcChain (its cells lie outside the stub
 *  grid, and Fortune's mount-time formula pass would throw on them), and
 *  shrinks the grid to stub dimensions. */
export const makeStubSheet = (sheet: FortuneSheet): FortuneSheet => ({
    ...sheet,
    calcChain: undefined,
    celldata: [],
    row: STUB_ROWS,
    column: STUB_COLS,
});

export interface UseLazySheetsArgs {
    /** Fully-resolved sheets (celldata form). */
    sheets: FortuneSheet[] | null | undefined;
    workbookRef: RefObject<WorkbookInstance>;
    /** Test seam; defaults to LAZY_HYDRATION_CELL_THRESHOLD. */
    cellThreshold?: number;
    /**
     * Set to `true` for the duration of each hydration `updateSheet` call, then
     * back to `false` — `updateSheet` goes through the same `onOp`/emitOp path a
     * real edit does, and a stub-fill's ops are path-identical to a real edit
     * (both write into `data`/`celldata`), so the caller's dirty-tracking (e.g.
     * markWorkbookDirty) reads this ref to tell the two apart. Owned by the
     * caller; a hook-local ref is used when none is passed (read-only viewers
     * with no dirty-tracking, existing tests).
     */
    suppressDirtyRef?: MutableRefObject<boolean>;
    /** Once the workbook settles, prepare the formula work its first edit would otherwise do inside the edit
     *  (prepareFormulasInSlices). For editable workbooks; read-only views never pay for it. */
    prepareFormulaEdits?: boolean;
}

/**
 * Deferred per-sheet hydration for large workbooks. Below the threshold this is
 * a pass-through. Above it, only the active sheet mounts with content; the
 * other tabs mount as empty stubs, and `hydratePending` (called by the host
 * component right after the Workbook mounts) fills them in one per tick via
 * workbookRef.updateSheet(). Deliberately NOT hooked to tab activation — the
 * first shipped version hydrated on Fortune-Sheet's afterActivateSheet hook,
 * which left every stub blank whenever activation semantics didn't line up
 * (e.g. no sheet carried status === 1, so OUR stubbing and the library's
 * ensureSheetIndex disagreed about which sheet was active).
 *
 * Callers that read sheet content back out (save/download/add-to-reports) MUST
 * still route getAllSheets() through `getFullSheets` — in the brief window
 * before hydration completes, a stub persisted raw would wipe that sheet.
 */
export const useLazySheets = ({
    sheets,
    workbookRef,
    cellThreshold = LAZY_HYDRATION_CELL_THRESHOLD,
    suppressDirtyRef: suppressDirtyRefProp,
    prepareFormulaEdits = false,
}: UseLazySheetsArgs) => {
    // Sheets awaiting hydration for the last COMMITTED render, keyed by sheet
    // id. Written only from the effect below — never from useMemo — so a
    // render React replays or discards can never mutate the queue a committed
    // workbook already has a drain scheduled against (see pendingMap).
    const pendingRef = useRef<Map<string, FortuneSheet>>(new Map());
    const tailRef = useRef<Map<string, FortuneCellData[]>>(new Map());
    const uncachedRef = useRef<UncachedFormula[]>([]);
    const ownSuppressDirtyRef = useRef(false);
    // The queue a drain is running against, so a repeat hydratePending call doesn't start a second one.
    const drainingRef = useRef<Map<string, FortuneSheet> | null>(null);
    // Read when the workbook settles, so toggling it never re-runs the caller's hydration effect.
    const prepareFormulaEditsRef = useRef(prepareFormulaEdits);
    prepareFormulaEditsRef.current = prepareFormulaEdits;
    const suppressDirtyRef = suppressDirtyRefProp ?? ownSuppressDirtyRef;
    // Row heights fitted to wrapped text on open, per sheet id: display only, dropped by getFullSheets.
    const autoRowHeightsRef = useRef<Map<string, Record<number, number>>>(new Map());
    const heldRowHeightsRef = useRef<Map<string, Record<number, number>>>(new Map());

    const { workbookSheets, pendingMap, tailMap, uncached } = useMemo(() => {
        const tails = new Map<string, FortuneCellData[]>();
        const trimmedSheets = sheets?.map((rawSheet) => {
            const sheet = withCoveringDims(rawSheet);
            const { sheet: trimmed, tail } = splitSparseTail(sheet);
            if (!tail.length) return trimmed;
            tails.set(String(sheet.id), tail);
            return clipCalcChainToGrid(trimmed);
        });
        const unchanged = !tails.size && trimmedSheets?.every((sheet, i) => sheet === sheets?.[i]);
        const resolvedSheets = unchanged ? sheets : trimmedSheets;
        const uncached = findUncachedFormulas(resolvedSheets);
        if (!resolvedSheets || resolvedSheets.length < 2 || totalCells(resolvedSheets) <= cellThreshold) {
            resolvedSheets?.forEach(freezeSheetPayload);
            return { workbookSheets: resolvedSheets, pendingMap: new Map<string, FortuneSheet>(), tailMap: tails, uncached };
        }
        // Mirror Fortune-Sheet's ensureSheetIndex: when no sheet carries
        // status === 1, the library activates the FIRST sheet at mount — so
        // that is the one sheet that must never be a stub. Stubbing by
        // `status !== 1` alone blanked the whole workbook for saved/composed
        // sheet arrays where every status was 0.
        const statusActive = resolvedSheets.findIndex((s) => Number(s.status) === 1);
        const activeIndex = statusActive === -1 ? 0 : statusActive;
        const pending = new Map<string, FortuneSheet>();
        const stubbed = resolvedSheets.map((sheet, i) => {
            if (i === activeIndex) return sheet;
            pending.set(String(sheet.id), sheet);
            return makeStubSheet(sheet);
        });
        // Frozen now, as the mount's commit would freeze them: its immer pass then skips the
        // borders and styles instead of walking them (~2 s on a 44-tab, 800k-cell workbook).
        stubbed.forEach(freezeSheetPayload);
        return { workbookSheets: stubbed, pendingMap: pending, tailMap: tails, uncached };
    }, [sheets, cellThreshold]);

    // Publish the derived map only once this render has committed. A render
    // React discards (StrictMode double-render, an abandoned concurrent
    // render) still runs the useMemo above but never reaches this effect, so
    // pendingRef always reflects the committed workbook.
    useEffect(() => {
        pendingRef.current = pendingMap;
        tailRef.current = tailMap;
        uncachedRef.current = uncached;
        autoRowHeightsRef.current = new Map();
        heldRowHeightsRef.current = new Map();
    }, [pendingMap, tailMap, uncached]);

    /**
     * Drain all pending stubs into the live workbook, one sheet at a time, then
     * recalculate formulas that arrived without a cached value (needs every stub
     * hydrated first so cross-sheet references resolve). Known limit: formulas parked
     * outside the grid by splitSparseTail are not recalculated.
     * Each sheet's grid is built in short slices (createSheetBuild) and committed in one
     * cheap state update, so a click or key press never waits behind a whole tab. The
     * sheet stays in the queue, and getFullSheets keeps handing back its original, until
     * that commit. The tab on screen goes first: it shows an empty stub until filled.
     * Idempotent and safe to call repeatedly (StrictMode double-effects): one drain runs
     * per queue. Captures the queue once per call — if a later commit publishes a new
     * map before this drain finishes, `pendingRef.current` no longer matches the
     * captured `queue` and the stale drain stops instead of touching the new workbook.
     */
    const hydratePending = useCallback(() => {
        const queue = pendingRef.current;
        const uncached = uncachedRef.current;
        const isStale = () => pendingRef.current !== queue;
        const prepare = () => {
            if (prepareFormulaEditsRef.current && !isStale())
                prepareFormulasInSlices({ getContext: () => getInternals(workbookRef.current)?.getContext(), isStale });
        };
        const recalc = () => recalculateUntilStable({ workbookRef, uncached, suppressDirtyRef, isStale, onSettled: prepare });
        const stop = () => {
            if (drainingRef.current === queue) drainingRef.current = null;
        };
        // Excel's open-time row fit for wrapped text (RECUR-28432): measured in slices and applied in a
        // few quiet updates, the first rows at once, so the rows on screen settle without waiting for the sheet.
        const fitRows = (ids: string[], onDone: () => void) => {
            const ctx = getInternals(workbookRef.current)?.getContext();
            const measure = ctx && createTextHeightMeasure(ctx);
            const id = ids[0];
            const index = ctx?.luckysheetfile && id != null ? getSheetIndex(ctx, id) : null;
            const file = ctx && index != null ? ctx.luckysheetfile[index] : undefined;
            const next = () => (ids.length > 1 ? fitRows(ids.slice(1), onDone) : onDone());
            if (!ctx || !measure || !file?.data) {
                if (id == null || !ctx || !measure) onDone();
                else next();
                return;
            }
            const data = file.data;
            const job = createRowAutofitJob(
                data,
                file.config ?? {},
                { rowHeight: ctx.defaultrowlen, colWidth: ctx.defaultcollen },
                measure,
            );
            let applies = 0;
            let lastApplyAt = 0;
            // false when the workbook or the sheet is gone
            const apply = (): boolean => {
                const wb = workbookRef.current;
                const live = wb && getInternals(wb)?.getContext();
                const liveIndex = live ? getSheetIndex(live, id) : null;
                const liveData = live && liveIndex != null ? live.luckysheetfile[liveIndex].data : undefined;
                if (!wb || !liveData) return false;
                const measured = job.takeHeights();
                const rows: Record<number, number> = {};
                Object.keys(measured).forEach((key) => {
                    const r = Number(key);
                    // Only the row that was measured, still in its place; a row edited, inserted or moved since keeps today's height.
                    if (liveData[r] === data[r]) rows[r] = measured[r];
                });
                applies += 1;
                lastApplyAt = performance.now();
                if (!Object.keys(rows).length) return true;
                produceQuietly(
                    wb,
                    (draft) => {
                        const at = getSheetIndex(draft, id);
                        if (at == null) return;
                        const added = addRowHeights(draft, at, rows);
                        autoRowHeightsRef.current.set(id, { ...autoRowHeightsRef.current.get(id), ...added });
                        // On the tab on screen, keep the rows the user is looking at where they are, with the
                        // row offsets recomputed in this same update so the next paint matches the new heights.
                        const file = draft.luckysheetfile[at];
                        if (draft.currentSheetId === file.id && file.data) {
                            keepRowsOnScreen(draft, added);
                            refreshRowColOffsets(draft, file.data);
                        }
                    },
                    () => undefined,
                );
                return true;
            };
            const slice = () => {
                if (isStale()) return;
                const end = performance.now() + HYDRATE_SLICE_MS;
                let finished = job.step();
                while (!finished && performance.now() < end) finished = job.step();
                const due =
                    applies === 0 || performance.now() - lastApplyAt >= (applies < 3 ? ROW_AUTOFIT_FIRST_APPLY_MS : ROW_AUTOFIT_APPLY_MS);
                if ((finished || due) && !apply()) {
                    if (workbookRef.current) next();
                    else onDone();
                    return;
                }
                if (finished) next();
                else setTimeout(slice, 0);
            };
            setTimeout(slice, 0);
        };
        const loadedSheetIds = () => {
            const ctx = getInternals(workbookRef.current)?.getContext();
            if (!ctx?.luckysheetfile) return [];
            const ids = ctx.luckysheetfile.map((file) => String(file.id)).filter((id) => !queue.has(id));
            const current = String(ctx.currentSheetId);
            return ids.includes(current) ? [current, ...ids.filter((id) => id !== current)] : ids;
        };
        const scheduleNext = () => {
            if (queue.size) setTimeout(drain, HYDRATE_TICK_MS);
            else {
                stop();
                // let the last hydration commit first
                setTimeout(uncached.length ? recalc : prepare, HYDRATE_TICK_MS);
            }
        };
        const afterCommit = (id: string) => fitRows([id], scheduleNext);
        const commit = (id: string, sheet: FortuneSheet, prepared: PreparedSheet | null): boolean => {
            const wb = workbookRef.current;
            if (!wb || isStale() || !queue.has(id)) return false;
            // Suppressed synchronously around the call — the public updateSheet emits onOp ops
            // path-identical to a real edit (see suppressDirtyRef above).
            const release = holdDirtySuppression(suppressDirtyRef);
            try {
                // A row/column insert, delete or undo nulls Fortune's formula map so the next edit
                // rebuilds it from every sheet's calcChain; updateSheet would otherwise recreate it
                // holding only this sheet's formulas, and the rebuild would never fire.
                const mapWasEmpty = isEmpty(getInternals(wb)?.getContext().formulaCache.formulaCellInfoMap);
                // Quiet: hydration is bookkeeping — no undo entry (Ctrl+Z would re-stub the tab and
                // arm autosave with an empty sheet) and no onOp emission.
                produceQuietly(
                    wb,
                    (ctx) => {
                        // updateSheet's celldata path, with the grid and formula entries prepared in
                        // slices; it keeps the stub's config (borders/merges/widths) intact.
                        if (!prepared || !commitPreparedSheet(ctx, sheet, prepared, !mapWasEmpty)) core.api.updateSheet(ctx, [sheet]);
                        // updateSheet's celldata path registers the formulas but never copies the
                        // payload's calcChain, so restore it or the sheet's formulas stop recalculating.
                        const index = getSheetIndex(ctx, id);
                        const file = index == null ? undefined : ctx.luckysheetfile[index];
                        if (file && sheet.calcChain?.length) file.calcChain = sheet.calcChain;
                        // ...nor its row/column: the stub's 50×26 stayed, and Ctrl+Arrow stopped at row 50.
                        if (file) {
                            file.row = Math.max(sheet.row ?? 0, file.data?.length ?? 0);
                            file.column = Math.max(sheet.column ?? 0, file.data?.[0]?.length ?? 0);
                        }
                        if (mapWasEmpty) ctx.formulaCache.formulaCellInfoMap = null;
                    },
                    () => wb.updateSheet([sheet]),
                );
            } finally {
                release();
            }
            queue.delete(id);
            return true;
        };
        const drain = () => {
            const wb = workbookRef.current;
            if (!wb || isStale() || queue.size === 0) {
                stop();
                return;
            }
            const internals = getInternals(wb);
            const currentId = String(internals?.getContext().currentSheetId ?? '');
            const id = queue.has(currentId) ? currentId : (queue.keys().next().value as string);
            const sheet = queue.get(id) as FortuneSheet;
            if (!internals) {
                if (commit(id, sheet, null)) afterCommit(id);
                else stop();
                return;
            }
            const build = createSheetBuild(sheet, () => getInternals(workbookRef.current)?.getContext());
            runInSlices(
                build.step,
                () => {
                    if (commit(id, sheet, build.prepared())) afterCommit(id);
                    else stop();
                },
                isStale,
            );
        };
        if (queue.size) {
            if (drainingRef.current === queue) return;
            drainingRef.current = queue;
            setTimeout(() => fitRows(loadedSheetIds(), drain), FIRST_HYDRATE_DELAY_MS);
        } else setTimeout(() => fitRows(loadedSheetIds(), uncached.length ? recalc : prepare), FIRST_HYDRATE_DELAY_MS);
    }, [workbookRef, suppressDirtyRef]);

    const formulaPrepTimerRef = useRef<number>();
    useEffect(() => () => window.clearTimeout(formulaPrepTimerRef.current), []);
    /** Called on every content edit; does work only when the formula map was left empty and hydration is done. */
    const prepareFormulasAfterEdit = useCallback(() => {
        if (!prepareFormulaEditsRef.current) return;
        window.clearTimeout(formulaPrepTimerRef.current);
        const queue = pendingRef.current;
        formulaPrepTimerRef.current = window.setTimeout(() => {
            if (queue.size || pendingRef.current !== queue) return; // still hydrating: its own preparation follows
            prepareFormulasInSlices({
                getContext: () => getInternals(workbookRef.current)?.getContext(),
                isStale: () => pendingRef.current !== queue,
            });
        }, FORMULA_PREP_AFTER_EDIT_MS);
    }, [workbookRef]);

    /** Replace still-stubbed live sheets with their full pending originals, drop the row heights
     *  fitted on open, and hand back the cells parked outside the grid by splitSparseTail. */
    const getFullSheets = useCallback((live: FortuneSheet[]): FortuneSheet[] => {
        if (!pendingRef.current.size && !tailRef.current.size && !autoRowHeightsRef.current.size) return live;
        return live.map((liveSheet) => {
            const id = String(liveSheet.id);
            const sheet = pendingRef.current.get(id) ?? withoutAutoRowHeights(liveSheet, autoRowHeightsRef.current.get(id));
            const tail = tailRef.current.get(id);
            if (!tail) return sheet;
            const withCelldata = toCelldataSheet(sheet);
            return { ...withCelldata, celldata: [...(withCelldata.celldata ?? []), ...tail] };
        });
    }, []);

    /** Pass every onOp batch: keeps the fitted heights on their rows across row inserts and deletes. */
    const noteRowOps = useCallback((ops: Parameters<typeof shiftAutoRowHeights>[2]) => {
        shiftAutoRowHeights(autoRowHeightsRef.current, heldRowHeightsRef.current, ops);
    }, []);

    return { workbookSheets, getFullSheets, hydratePending, prepareFormulasAfterEdit, noteRowOps };
};
